// Superset column model for the spreadsheet.
//
// A filter can return entries of several content types with different fields. The sheet shows
// the UNION of all their fields as columns; a cell that does not apply to a given row's content
// type is rendered greyed-out and read-only. Localized fields expand to one column per locale.
//
// Column identity is `fields.<fieldId>.<locale>`. Where two content types share a field id with
// different definitions, the column keeps the first-seen definition only for labelling - the
// authoritative field definition for encoding/decoding a cell is always looked up from the
// *row's* content type via fieldDefFor().

export type SpreadsheetColumn = {
	key: string          // stable identity, e.g. 'sys.id' or 'fields.title.en-US'
	label: string        // header text
	kind: 'sys' | 'field'
	readOnly: boolean
	fieldId?: string     // for kind === 'field'
	locale?: string      // for kind === 'field'
	field?: any          // first-seen content type field definition (labelling only)
}

function contentTypeIdOf(entry: any): string | undefined {
	return entry?.sys?.contentType?.sys?.id
}

// Distinct content type ids present in the loaded entries, in first-seen order.
export function contentTypeIdsOf(entries: any[]): string[] {
	const seen: string[] = []
	for (const entry of entries) {
		const id = contentTypeIdOf(entry)
		if (id && !seen.includes(id)) seen.push(id)
	}
	return seen
}

// The content type field definition governing a given cell for a specific entry, or undefined
// if the entry's content type does not define that field (=> cell is not applicable).
export function fieldDefFor(typeLookup: any, entry: any, fieldId: string): any {
	const typeId = contentTypeIdOf(entry)
	const ct = typeLookup?.[typeId as string]
	return ct?.fields?.find((f: any) => f.id === fieldId)
}

export function buildColumns(
	entries: any[],
	typeLookup: any,
	locales: any[],
	defaultLocale: string,
): { columns: SpreadsheetColumn[], typeIds: string[], multiType: boolean } {
	const typeIds = contentTypeIdsOf(entries)
	const multiType = typeIds.length > 1
	const localeCodes = (locales ?? []).map(l => l.code)

	const columns: SpreadsheetColumn[] = []
	columns.push({ key: 'sys.id', label: 'ID', kind: 'sys', readOnly: false })
	// Type is always shown (col 2). It is locked for loaded rows; newly-added rows render a
	// type selector in this cell (see SpreadsheetGrid) so the row's content type can be chosen.
	columns.push({ key: 'sys.contentType', label: 'Type', kind: 'sys', readOnly: true })

	const seen = new Set<string>()
	for (const typeId of typeIds) {
		const ct = typeLookup?.[typeId]
		for (const field of ct?.fields ?? []) {
			const fieldLocales = field.localized ? localeCodes : [ defaultLocale ]
			for (const locale of fieldLocales) {
				const key = `fields.${field.id}.${locale}`
				if (seen.has(key)) continue
				seen.add(key)
				columns.push({
					key,
					label: field.name + (field.localized ? ` (${locale})` : ''),
					kind: 'field',
					readOnly: false,
					fieldId: field.id,
					locale,
					field,
				})
			}
		}
	}
	return { columns, typeIds, multiType }
}
